import { ApolloError } from '@apollo/client';
import type { VerifyBatchOutcome, VerifyScanJob } from './verifyScanQueue';

/**
 * Sending the Verify screen's writes (the scan queue's `run` / `runBatch`) — pure, so every rule
 * below is pinned by verifySend.test.ts.
 *
 * A TIME LIMIT. A request that never answers (a dropped connection, a stuck server) used to hold the
 * one-at-a-time queue — and every scan behind it — for as long as the browser kept it open. Each
 * attempt now gives up after verifySendTimeoutMs (and aborts the request).
 *
 * RE-SENDS. A request that timed out or lost its connection may or may not have been applied, so it
 * is sent again — at most twice, a moment apart — with the SAME `clientScanId`s: the server records
 * each id with its write and answers a re-sent one instead of applying it again (a re-sent bulk unit
 * would otherwise be picked twice). A request the server ANSWERED — a refusal, an error — is never
 * re-sent. Requests sent without ids (an older server) are never re-sent either.
 *
 * NO ANSWER AT ALL. When every attempt went unanswered, the outcome is UNKNOWN — a frozen server may
 * still commit the first attempt — so it fails with VerifySendOutcomeUnknownError, never with an
 * ordinary error: the screen keeps the scan queued under its id and sends it again later, rather than
 * telling the operator it failed (a re-scan would carry a NEW id and could be picked twice). A
 * re-send refused as invalid (it reached a server that predates the ids, mid-deploy) is no answer
 * either: that server cannot tell it from a new scan, and the first attempt may have been applied.
 *
 * BATCHES. The server answers a batch with one result per unit (salesOrderVerifyScanBatch); each is
 * turned back into the outcome the queue delivers, a refusal into the very error shape a single
 * scan's refusal has (`graphQLErrors[0].extensions.code / reason`), so the screen handles it alike.
 *
 * AN OLDER SERVER. A server without the batch mutation, or without `clientScanId`, refuses the
 * request as invalid (GRAPHQL_VALIDATION_FAILED); the screen then sends as it did before.
 */

/**
 * One unit's request. Production's healthy scans answer in ~1.7 s (p99 ~6 s); the stalls it does
 * have are one API pod freezing for 12–41 s while every other pod answers normally — so a re-send
 * after 10 s (it lands on another pod) beats waiting the freeze out.
 */
export const VERIFY_SEND_TIMEOUT_MS = 10_000;
/** A batch: more units take longer to record. */
export const VERIFY_SEND_TIMEOUT_PER_UNIT_MS = 2_000;
export const VERIFY_SEND_MAX_TIMEOUT_MS = 30_000;
/** Pauses before the re-sends — two at most. */
export const VERIFY_SEND_RETRY_DELAYS_MS: readonly number[] = [1_000, 3_000];

/**
 * A request that got no answer at all is sent again after this pause, for up to this many rounds
 * (each round is up to three timed attempts) — a few minutes, well past the 60 s a MongoDB transaction
 * may live — before the screen gives up and asks the operator to check the re-read list.
 */
export const VERIFY_UNKNOWN_RESEND_DELAY_MS = 3_000;
export const VERIFY_UNKNOWN_RESEND_ROUNDS = 6;
export const VERIFY_SEND_GAVE_UP_MESSAGE =
  'No answer from the server for several minutes. The list was refreshed — check this product’s count before scanning it again.';

export const verifySendTimeoutMs = (units: number): number =>
  Math.min(
    VERIFY_SEND_MAX_TIMEOUT_MS,
    VERIFY_SEND_TIMEOUT_MS + Math.max(0, units - 1) * VERIFY_SEND_TIMEOUT_PER_UNIT_MS
  );

export class VerifySendTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`No answer from the server in ${Math.round(timeoutMs / 1000)} s.`);
    this.name = 'VerifySendTimeoutError';
  }
}

/** Every attempt went unanswered: the write may or may not have been applied (see the header). */
export class VerifySendOutcomeUnknownError extends Error {
  constructor(readonly lastFailure: unknown) {
    super('No answer from the server — the scan may or may not have been saved.');
    this.name = 'VerifySendOutcomeUnknownError';
  }
}

export const isVerifySendOutcomeUnknown = (error: unknown): boolean =>
  error instanceof VerifySendOutcomeUnknownError;

type GraphQLErrorLike = { message?: string; extensions?: { code?: string } | null };

/** Every GraphQL error on a failure — including a 400's, which Apollo files under `networkError.result`. */
const graphQLErrorsOf = (error: unknown): GraphQLErrorLike[] => {
  const e = error as {
    graphQLErrors?: unknown;
    networkError?: { result?: { errors?: unknown } } | null;
  } | null;
  const direct = Array.isArray(e?.graphQLErrors) ? (e!.graphQLErrors as GraphQLErrorLike[]) : [];
  const inBody = e?.networkError?.result?.errors;
  return [...direct, ...(Array.isArray(inBody) ? (inBody as GraphQLErrorLike[]) : [])];
};

/**
 * No answer the device can trust — so the request may have been applied, or not: a time-out, a
 * dropped connection, a gateway error. Never a GraphQL answer (a refusal, a validation error), and
 * never a 4xx (signed out, malformed) other than 408 / 429.
 */
export function isVerifySendRetryable(error: unknown): boolean {
  if (error instanceof VerifySendTimeoutError) return true;
  if (graphQLErrorsOf(error).length) return false;
  const networkError = (error as { networkError?: { statusCode?: unknown } | null } | null)?.networkError;
  if (!networkError) return false;
  const status = networkError.statusCode;
  if (typeof status === 'number' && status >= 400 && status < 500) return status === 408 || status === 429;
  return true;
}

/** A request the server refused as invalid for this build (a field / argument it does not know). */
const isSchemaRefusal = (error: unknown): boolean =>
  graphQLErrorsOf(error).some(
    (graphQLError) =>
      graphQLError?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' ||
      /^(Cannot query field|Unknown argument|Unknown type)/.test(
        typeof graphQLError?.message === 'string' ? graphQLError.message : ''
      )
  );

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Send one Verify write with a time limit per attempt; re-send it (same variables, so the same ids)
 * while it fails without an answer, at most `retryDelaysMs.length` times, then fail with
 * VerifySendOutcomeUnknownError. An answer — a result, a refusal — ends it, except a re-send refused
 * as invalid, which counts as no answer (see the header). `send` gets the attempt's abort signal
 * (passed to Apollo as `context.fetchOptions.signal`).
 */
export async function sendVerifyWrite<T>(
  send: (signal: AbortSignal | undefined) => Promise<T>,
  {
    timeoutMs,
    retryDelaysMs = VERIFY_SEND_RETRY_DELAYS_MS,
    sleep = defaultSleep
  }: {
    timeoutMs: number;
    retryDelaysMs?: readonly number[];
    sleep?: (ms: number) => Promise<void>;
  }
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        reject(new VerifySendTimeoutError(timeoutMs));
        controller?.abort();
      }, timeoutMs);
    });
    try {
      return await Promise.race([send(controller?.signal), timedOut]);
    } catch (error) {
      const unanswered = isVerifySendRetryable(error) || (attempt > 0 && isSchemaRefusal(error));
      if (!unanswered) throw error;
      if (attempt >= retryDelaysMs.length) throw new VerifySendOutcomeUnknownError(error);
      await sleep(retryDelaysMs[attempt]);
    } finally {
      clearTimeout(timer);
    }
  }
}

/**
 * The server does not know `name` (a field or argument this build sends): it predates the change,
 * e.g. while a deploy rolls out. The screen falls back to what it sent before. Only ever true of a
 * FIRST attempt's refusal (nothing of that request was applied): a re-send's ends in
 * VerifySendOutcomeUnknownError instead.
 */
export function isVerifySchemaMismatch(error: unknown, name: string): boolean {
  return graphQLErrorsOf(error).some((graphQLError) => {
    const message = typeof graphQLError?.message === 'string' ? graphQLError.message : '';
    return (
      message.includes(`"${name}"`) &&
      (graphQLError?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' ||
        /^(Cannot query field|Unknown argument|Unknown type)/.test(message))
    );
  });
}

/** salesOrderVerifyScanBatch's answer, as selected by the screen. */
export type VerifyBatchResponse = {
  results?: Array<{
    clientScanId: string;
    status: 'APPLIED' | 'REFUSED' | 'NOT_ATTEMPTED' | string;
    lineKey?: string | null;
    addedQuantity?: number | null;
    replayed?: boolean | null;
    error?: { message?: string | null; code?: string | null; reason?: string | null } | null;
  }> | null;
  list?: unknown;
} | null | undefined;

/** A unit's refusal from a batch, in the shape a single scan's refusal reaches the screen in. */
export function verifyBatchRefusalError(error: {
  message?: string | null;
  code?: string | null;
  reason?: string | null;
}): ApolloError {
  const message = error.message || 'The scan was refused.';
  return new ApolloError({
    graphQLErrors: [
      {
        message,
        extensions: { code: error.code || 'INTERNAL_SERVER_ERROR', ...(error.reason ? { reason: error.reason } : {}) }
      } as any
    ]
  });
}

/**
 * One outcome per job, in the order sent — matched by `clientScanId`, never by position. An APPLIED
 * unit delivers what a single scan's answer would (`{ lineKey, addedQuantity, removedQuantity, list }`,
 * with the batch's one list); a unit the answer does not mention is sent again (the queue fails the
 * first job instead, so nothing is re-sent forever).
 */
export function verifyBatchOutcomes<R>(
  response: VerifyBatchResponse,
  jobs: readonly VerifyScanJob[],
  wrap: (result: {
    lineKey: string | null;
    addedQuantity: number;
    removedQuantity: number;
    list: unknown;
  }) => R
): VerifyBatchOutcome<R>[] {
  const byId = new Map((response?.results || []).map((result) => [result.clientScanId, result]));
  return jobs.map((job) => {
    const result = job.clientScanId ? byId.get(job.clientScanId) : undefined;
    if (!result) return { kind: 'retry' };
    if (result.status === 'APPLIED') {
      return {
        kind: 'result',
        result: wrap({
          lineKey: result.lineKey ?? null,
          addedQuantity: result.addedQuantity ?? 0,
          removedQuantity: 0,
          list: response?.list
        })
      };
    }
    if (result.status === 'REFUSED') {
      return { kind: 'error', error: verifyBatchRefusalError(result.error || {}) };
    }
    return { kind: 'retry' };
  });
}
